/**
 * CTQ Thermal Performance mock data
 * Based on Performance_HP_CM-KS_FA608k_20260809.xlsx format
 * Columns: barcode, fixtureId, stationId, testTime, TH1, TH2, roomTemp,
 *          cpuPower, vgaPower, T1~T6, DT1(T1-T3), DT2(T2-T4), DT3, result, errorCode
 */

function rnd (min, max, digits = 1) {
  const v = min + Math.random() * (max - min)
  return Number(v.toFixed(digits))
}

function pad (n) {
  return n < 10 ? '0' + n : String(n)
}

function makeBarcode (model, i, golden = false) {
  const base = `${model}336006${pad(i)}-GP3YYMDSSSS`
  return golden ? `${base}-GOLDEN` : base
}

function makeRecord (project, dateStr, i, fail = false, golden = false) {
  const model = project.modelName
  const processType = project.processType
  const isHp = processType === 'HP'
  const t1 = rnd(48, 55, 2)
  const t2 = rnd(48, 55, 2)
  const t3 = rnd(44, 50, 2)
  const t4 = rnd(44, 50, 2)
  // HP：T5/T6、DT3；VC：僅 T1~T4、DT1~DT2
  const t5 = isHp ? rnd(40, 48, 2) : null
  const t6 = isHp ? rnd(40, 48, 2) : null
  let dt1 = rnd(2.5, 4.8, 2)
  let dt2 = rnd(2.5, 4.8, 2)
  let dt3 = isHp ? rnd(2.5, 4.8, 2) : null
  if (fail) {
    dt1 = rnd(5.1, 8.5, 2)
    dt2 = rnd(5.1, 7.5, 2)
    if (isHp) dt3 = rnd(5.1, 7.5, 2)
  }
  const hour = 8 + (i % 10)
  const min = (i * 7) % 60
  const sec = (i * 13) % 60
  const pk = `${model}_${processType}_${dateStr}_${i}`
  const launchTime = `${pad(hour)}:${pad(min)}:${pad(sec)}`
  return {
    pk,
    pkList: [pk],
    operation: '測試數據',
    barcode: makeBarcode(model, i, golden),
    fixtureId: String(1 + (i % 5)),
    stationId: String(1 + (i % 6)),
    testTime: `${dateStr} ${launchTime}`,
    launchTime,
    retry: 0,
    traceDate: dateStr,
    traceTime: launchTime,
    th1: rnd(58, 65),
    th2: rnd(68, 75),
    roomTemperature: rnd(24, 28),
    cpuPower: isHp ? 28 : 23,
    vgaPower: isHp ? 55 : 50,
    t1,
    t2,
    t3,
    t4,
    t5,
    t6,
    dt1,
    dt2,
    dt3,
    isGolden: golden,
    endResult: fail ? 'fail' : 'pass',
    testResult: fail ? 'fail' : 'pass',
    errorCode: fail ? 'DT_OVER' : 0,
    modelName: model,
    deviceIsn: project.deviceIsn,
    vendor: project.vendor,
    vendorPk: project.vendorPk,
    processType
  }
}

/** 僅 HP / VC */
const PROJECTS = [
  { modelName: 'FA608K', deviceIsn: 'FA608K-PN', vendor: 'CM-KS', vendorPk: 1, processType: 'HP' },
  { modelName: 'FA608K', deviceIsn: 'FA608K-PN', vendor: 'CM-KS', vendorPk: 1, processType: 'VC' },
  { modelName: 'FA608L', deviceIsn: 'FA608L-PN', vendor: 'CM-KS', vendorPk: 1, processType: 'HP' },
  { modelName: 'FA608L', deviceIsn: 'FA608L-PN', vendor: 'CM-KS', vendorPk: 1, processType: 'VC' },
  { modelName: 'HP608A', deviceIsn: 'HP608A-PN', vendor: 'INVENTEC_CQ', vendorPk: 2, processType: 'HP' },
  { modelName: 'HP608A', deviceIsn: 'HP608A-PN', vendor: 'INVENTEC_CQ', vendorPk: 2, processType: 'VC' },
  { modelName: 'VC608B', deviceIsn: 'VC608B-PN', vendor: 'INVENTEC_CQ', vendorPk: 2, processType: 'VC' }
]

function buildDayRecords (project, dateStr, total = 40, failQty = 2) {
  const list = []
  for (let i = 0; i < total; i++) {
    // 前 2 筆模擬 Golden（圖表統計需排除）
    const golden = i < 2
    list.push(makeRecord(
      project,
      dateStr,
      i + 1,
      !golden && i < failQty + 2,
      golden
    ))
  }
  return list
}

/** 供應商／料號為選填：空、null、All 皆視為不篩選 */
function matchOptional (selected, actual) {
  if (selected == null || selected === '' || selected === 'All') return true
  return selected === actual
}

/** 正態分佈圖用：僅 PASS，且排除 Golden */
export function isChartEligible (row) {
  if (!row) return false
  const result = String(row.endResult || row.testResult || '').toLowerCase()
  if (result !== 'pass') return false
  const barcode = String(row.barcode || '').toUpperCase()
  if (barcode.indexOf('GOLDEN') !== -1 || row.isGolden) return false
  return true
}

// Pre-build ~30 days of summary + sample detail for Aug 2026
const DETAIL_BY_KEY = {}
const DAILY_SUMMARY = []

function dateAdd (base, days) {
  const d = new Date(base)
  d.setDate(d.getDate() + days)
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  return `${y}-${m}-${day}`
}

const BASE = '2026-08-01'
for (let day = 0; day < 24; day++) {
  const dateStr = dateAdd(BASE, day)
  PROJECTS.forEach((p, pi) => {
    const failQty = (day + pi) % 5 === 0 ? 3 : (day % 3 === 0 ? 1 : 0)
    const total = 30 + ((day + pi) % 20)
    const records = buildDayRecords(p, dateStr, total, failQty)
    const key = `${p.deviceIsn}|${dateStr}|${p.vendorPk}|${p.processType}`
    DETAIL_BY_KEY[key] = records
    const failRate = total ? failQty / total : 0
    let warningColor = ''
    if (failRate > 0.03) warningColor = 'red'
    else if (failRate > 0.01) warningColor = 'orange'
    DAILY_SUMMARY.push({
      date: dateStr,
      modelName: p.modelName,
      deviceIsn: p.deviceIsn,
      vendor: p.vendor,
      vendorPk: p.vendorPk,
      processType: p.processType,
      productionQty: total,
      failQty,
      failRate,
      warningColor
    })
  })
}

export const PROCESS_TYPE_OPTIONS = [
  { text: 'HP', value: 'HP' },
  { text: 'VC', value: 'VC' }
]

/** 選填：無 All，可清空 */
export const VENDOR_OPTIONS = [
  { text: 'CM-KS', value: 'CM-KS' },
  { text: 'INVENTEC_CQ', value: 'INVENTEC_CQ' }
]

const UNIQUE_DEVICE_ISN = Array.from(new Set(PROJECTS.map(p => p.deviceIsn)))
export const DEVICE_ISN_OPTIONS = UNIQUE_DEVICE_ISN.map(v => ({ text: v, value: v }))

const UNIQUE_MODEL = Array.from(new Set(PROJECTS.map(p => p.modelName)))
export const MODEL_OPTIONS = [
  { text: 'All', value: 'All' },
  ...UNIQUE_MODEL.map(v => ({ text: v, value: v }))
]

/** 依供應商連動製程別選項（未選供應商＝HP/VC） */
export function getProcessTypeOptions (vendor) {
  const rows = PROJECTS.filter(p => matchOptional(vendor, p.vendor))
  const order = ['HP', 'VC']
  const set = new Set(rows.map(p => p.processType))
  return order.filter(v => set.has(v)).map(v => ({ text: v, value: v }))
}

/** 依供應商＋製程別連動料號選項 */
export function getDeviceIsnOptions (vendor, processType) {
  const rows = PROJECTS.filter(p => {
    if (!matchOptional(vendor, p.vendor)) return false
    if (processType && p.processType !== processType) return false
    return true
  })
  return Array.from(new Set(rows.map(p => p.deviceIsn))).map(v => ({ text: v, value: v }))
}

export function getDailySummary ({ date, vendor = null, deviceIsn = null, processType = 'HP', modelName = 'All', failRateMin = null }) {
  return DAILY_SUMMARY.filter(r => {
    if (date && r.date !== date) return false
    if (processType && r.processType !== processType) return false
    if (!matchOptional(vendor, r.vendor)) return false
    if (!matchOptional(deviceIsn, r.deviceIsn)) return false
    if (modelName && modelName !== 'All' && r.modelName !== modelName) return false
    if (failRateMin != null && failRateMin !== 'All' && r.failRate <= Number(failRateMin)) return false
    return true
  })
}

export function getMonthSummary ({ month, vendor = null, deviceIsn = null, processType = 'HP' }) {
  // month: YYYY-MM
  const rows = DAILY_SUMMARY.filter(r => {
    if (!String(r.date).startsWith(month)) return false
    if (processType && r.processType !== processType) return false
    if (!matchOptional(vendor, r.vendor)) return false
    if (!matchOptional(deviceIsn, r.deviceIsn)) return false
    return true
  })
  // calendar day map: date -> failList
  const byDate = {}
  rows.forEach(r => {
    if (!byDate[r.date]) byDate[r.date] = []
    if (r.failQty > 0) {
      byDate[r.date].push({
        deviceIsn: r.deviceIsn,
        modelName: r.modelName,
        vendor: r.vendor,
        vendorPk: r.vendorPk,
        processType: r.processType,
        failRate: r.failRate,
        warningColor: r.warningColor
      })
    }
  })
  // aggregate by 料號 + 供應商 + 製程別
  const agg = {}
  rows.forEach(r => {
    const k = r.deviceIsn + '|' + r.vendorPk + '|' + r.processType
    if (!agg[k]) {
      agg[k] = {
        deviceIsn: r.deviceIsn,
        modelName: r.modelName,
        vendor: r.vendor,
        vendorPk: r.vendorPk,
        processType: r.processType,
        productionQty: 0,
        failQty: 0
      }
    }
    agg[k].productionQty += r.productionQty
    agg[k].failQty += r.failQty
  })
  const table = Object.values(agg).map(a => {
    const failRate = a.productionQty ? a.failQty / a.productionQty : 0
    let warningColor = ''
    if (failRate > 0.03) warningColor = 'red'
    else if (failRate > 0.01) warningColor = 'orange'
    return { ...a, failRate, warningColor }
  })
  return { byDate, table, dailyRows: rows }
}

export function getWeekSummary ({ startDate, endDate, vendor = null, deviceIsn = null, processType = 'HP' }) {
  return DAILY_SUMMARY.filter(r => {
    if (r.date < startDate || r.date > endDate) return false
    if (processType && r.processType !== processType) return false
    if (!matchOptional(vendor, r.vendor)) return false
    if (!matchOptional(deviceIsn, r.deviceIsn)) return false
    return true
  })
}

export function getStatisticRecords ({ deviceIsn, date, dateRange, vendorPk, processType }) {
  const end = dateRange || date
  const keys = Object.keys(DETAIL_BY_KEY).filter(k => {
    const [dIsn, dDate, vPk, pType] = k.split('|')
    if (deviceIsn && dIsn !== deviceIsn) return false
    if (vendorPk != null && vendorPk !== '' && String(vPk) !== String(vendorPk)) return false
    if (processType && pType !== processType) return false
    if (date && (dDate < date || dDate > end)) return false
    return true
  })
  let list = []
  keys.forEach(k => { list = list.concat(DETAIL_BY_KEY[k]) })
  // fallback: generate if empty
  if (!list.length && deviceIsn && date) {
    const p = PROJECTS.find(x =>
      x.deviceIsn === deviceIsn &&
      (!processType || x.processType === processType)
    ) || PROJECTS[0]
    list = buildDayRecords(p, date, 40, 2)
  }
  const productionQty = list.length
  const failQty = list.filter(r => r.endResult === 'fail').length
  const failRate = productionQty ? failQty / productionQty : 0
  let warningColor = ''
  if (failRate > 0.03) warningColor = 'red'
  else if (failRate > 0.01) warningColor = 'orange'
  return {
    deviceList: list,
    summaryInfo: {
      productionQty,
      failQty,
      failRate,
      warningColor
    }
  }
}

export function searchByBarcode (barcode) {
  if (!barcode) return []
  const q = String(barcode).toUpperCase()
  const out = []
  Object.keys(DETAIL_BY_KEY).forEach(k => {
    DETAIL_BY_KEY[k].forEach(r => {
      if (String(r.barcode).toUpperCase().indexOf(q) !== -1) out.push(r)
    })
  })
  return out
}

export function getByPk (pk) {
  for (var k of Object.keys(DETAIL_BY_KEY)) {
    const hit = DETAIL_BY_KEY[k].find(r => String(r.pk) === String(pk))
    if (hit) return hit
  }
  return null
}

/** 單筆詳情：結構對齊 Thermal Module singleDeviceBurnInDataStatistic（欄位改為 Performance） */
export function getSingleDeviceStatistic (pk) {
  const hit = getByPk(pk)
  if (!hit) return null
  const day = getStatisticRecords({
    deviceIsn: hit.deviceIsn,
    date: hit.traceDate,
    vendorPk: hit.vendorPk,
    processType: hit.processType
  })
  const eligible = (day.deviceList || []).filter(isChartEligible)
  return {
    launchDate: hit.traceDate,
    launchTime: hit.launchTime || hit.traceTime,
    deviceIsn: hit.deviceIsn,
    modelName: hit.modelName,
    vendor: hit.vendor,
    vendorPk: hit.vendorPk,
    processType: hit.processType,
    fixtureId: hit.fixtureId,
    stationId: hit.stationId,
    roomTemperature: hit.roomTemperature,
    barcode: hit.barcode,
    testResult: hit.testResult || hit.endResult,
    errorMessage: hit.errorCode && hit.errorCode !== 0 ? String(hit.errorCode) : '',
    // DT1 / DT2 對應原 CPU R / VGA R
    dt1Spec: 5,
    dt1: hit.dt1,
    t1: hit.t1,
    t3: hit.t3,
    dt1TempDiff: Number((hit.t1 - hit.t3).toFixed(2)),
    cpuPower: hit.cpuPower,
    dt2Spec: 5,
    dt2: hit.dt2,
    t2: hit.t2,
    t4: hit.t4,
    dt2TempDiff: Number((hit.t2 - hit.t4).toFixed(2)),
    vgaPower: hit.vgaPower,
    th1: hit.th1,
    th2: hit.th2,
    t5: hit.t5,
    t6: hit.t6,
    dt3: hit.dt3,
    dt3Spec: 5,
    // 圖表用當日 PASS/非Golden 清單（沿用原頁 cpuRList / vgaRList 欄位名）
    cpuR: hit.dt1,
    vgaR: hit.dt2,
    cpuRList: eligible.map(r => r.dt1),
    vgaRList: eligible.map(r => r.dt2)
  }
}

/** 共用可選欄（選擇欄位）：機台號、站別、TH1/TH2、功率 */
const OPTIONAL_COLUMNS = [
  { text: '機台號', value: 'fixtureId', fixed: false, sortable: true, class: 'table-width-100', align: '' },
  { text: '站別', value: 'stationId', fixed: false, sortable: true, class: 'table-width-75', align: '' },
  { text: 'TH1', value: 'th1', fixed: false, sortable: true, class: 'table-width-100', align: '' },
  { text: 'TH2', value: 'th2', fixed: false, sortable: true, class: 'table-width-100', align: '' },
  { text: 'CPU實際功率', value: 'cpuPower', fixed: false, sortable: true, class: 'table-width-100', align: '' },
  { text: 'VGA實際功率', value: 'vgaPower', fixed: false, sortable: true, class: 'table-width-100', align: '' }
]

/**
 * VC 預設：BarCode、T1~T4、DT1~DT2、室溫、測試結果、測試數據
 * HP 預設：BarCode、T1~T6、DT1~DT3、室溫、測試結果、測試數據
 */
export function getColumnDefine (processType) {
  const isHp = processType === 'HP'
  const fixed = [
    { text: 'BarCode', value: 'barcode', fixed: true, sortable: true, class: 'table-width-300', align: '' },
    { text: 'T1', value: 't1', fixed: true, sortable: true, class: 'table-width-100', align: '' },
    { text: 'T2', value: 't2', fixed: true, sortable: true, class: 'table-width-100', align: '' },
    { text: 'T3', value: 't3', fixed: true, sortable: true, class: 'table-width-100', align: '' },
    { text: 'T4', value: 't4', fixed: true, sortable: true, class: 'table-width-100', align: '' }
  ]
  if (isHp) {
    fixed.push(
      { text: 'T5', value: 't5', fixed: true, sortable: true, class: 'table-width-100', align: '' },
      { text: 'T6', value: 't6', fixed: true, sortable: true, class: 'table-width-100', align: '' }
    )
  }
  fixed.push(
    { text: 'DT1', value: 'dt1', fixed: true, sortable: true, class: 'table-width-100', align: '' },
    { text: 'DT2', value: 'dt2', fixed: true, sortable: true, class: 'table-width-100', align: '' }
  )
  if (isHp) {
    fixed.push(
      { text: 'DT3', value: 'dt3', fixed: true, sortable: true, class: 'table-width-100', align: '' }
    )
  }
  fixed.push(
    { text: '室溫', value: 'roomTemperature', fixed: true, sortable: true, class: 'table-width-75', align: '' },
    { text: '測試結果', value: 'endResult', fixed: true, sortable: true, class: 'table-width-100', align: '' },
    { text: '測試數據', value: 'operation', fixed: true, sortable: false, class: 'table-width-120', align: '' }
  )
  return fixed.concat(OPTIONAL_COLUMNS.map(c => Object.assign({}, c)))
}

/** 圖表資料來源選項（依製程別） */
export function getChartItemOptions (processType) {
  if (processType === 'HP') {
    return [
      { text: 'DT1', value: 'dt1' },
      { text: 'DT2', value: 'dt2' },
      { text: 'DT3', value: 'dt3' },
      { text: 'T1', value: 't1' },
      { text: 'T2', value: 't2' },
      { text: 'T3', value: 't3' },
      { text: 'T4', value: 't4' },
      { text: 'T5', value: 't5' },
      { text: 'T6', value: 't6' },
      { text: 'TH1', value: 'th1' },
      { text: 'TH2', value: 'th2' }
    ]
  }
  return [
    { text: 'DT1', value: 'dt1' },
    { text: 'DT2', value: 'dt2' },
    { text: 'T1', value: 't1' },
    { text: 'T2', value: 't2' },
    { text: 'T3', value: 't3' },
    { text: 'T4', value: 't4' },
    { text: 'TH1', value: 'th1' },
    { text: 'TH2', value: 'th2' }
  ]
}

/** @deprecated 請用 getColumnDefine(processType) */
export const COLUMN_DEFINE = getColumnDefine('VC')

export default {
  VENDOR_OPTIONS,
  DEVICE_ISN_OPTIONS,
  MODEL_OPTIONS,
  PROCESS_TYPE_OPTIONS,
  getProcessTypeOptions,
  getDeviceIsnOptions,
  getColumnDefine,
  getChartItemOptions,
  getDailySummary,
  getMonthSummary,
  getWeekSummary,
  getStatisticRecords,
  searchByBarcode,
  getByPk,
  getSingleDeviceStatistic,
  isChartEligible,
  COLUMN_DEFINE,
  PROJECTS
}
